import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { BASE_URL } from "shared/lib/config";
import Header from "shared/ui/Header";



/* --------------------------- tiny helpers --------------------------- */

const getStore = () => {
  if (sessionStorage.getItem("idToken")) return sessionStorage;
  if (localStorage.getItem("idToken")) return localStorage;
  return null;
};

const normalizeGender = (g) => {
  if (!g) return "";
  const s = String(g).trim().toLowerCase();
  if (["male", "laki-laki", "laki", "boy", "m", "l"].includes(s)) return "male";
  if (["female", "perempuan", "wanita", "girl", "f", "p"].includes(s)) return "female";
  return "";
};

const fmtDate = (d) => {
  try {
    const dt = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
    if (!dt || isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

// Very small cache wrapper (sessionStorage so it clears on browser close)
const CACHE_LIST_KEY = "assessments:list";
const CACHE_BY_VOUCHER_KEY = "assessments:byVoucher";
const CACHE_OWNER_KEY = "assessments:owner";

const readCache = (ownerId) => {
  try {
    const currentOwner = sessionStorage.getItem(CACHE_OWNER_KEY);
    if (!ownerId || !currentOwner || currentOwner !== ownerId) {
      return { list: [], byVoucher: {} };
    }

    const list = JSON.parse(sessionStorage.getItem(CACHE_LIST_KEY) || "[]");
    const byVoucher = JSON.parse(sessionStorage.getItem(CACHE_BY_VOUCHER_KEY) || "{}");
    return { list, byVoucher };
  } catch {
    return { list: [], byVoucher: {} };
  }
};

const writeCache = (ownerId, rows) => {
  const byVoucher = {};
  for (const r of rows) {
    if (r?.voucher) byVoucher[r.voucher] = r;
  }
  sessionStorage.setItem(CACHE_OWNER_KEY, ownerId || "");
  sessionStorage.setItem(CACHE_LIST_KEY, JSON.stringify(rows));
  sessionStorage.setItem(CACHE_BY_VOUCHER_KEY, JSON.stringify(byVoucher));
  return { list: rows, byVoucher };
};

// Replace your existing SkeletonCard with this:
const SkeletonCard = () => (
  <div className="animate-pulse bg-white rounded-2xl border border-slate-200 shadow-md p-4 sm:p-6 md:p-7 min-h-[152px]">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-slate-200" />
      <div className="flex-1">
        <div className="h-4 w-40 bg-slate-200 rounded mb-2" />
        <div className="h-3 w-28 bg-slate-200 rounded" />
      </div>
      <div className="w-8 h-8 rounded-full bg-slate-100" />
    </div>
    <div className="mt-4 flex flex-wrap gap-2">
      <div className="h-6 w-28 bg-slate-100 rounded-full" />
      <div className="h-6 w-20 bg-slate-100 rounded-full" />
      <div className="h-6 w-24 bg-slate-100 rounded-full" />
    </div>
  </div>
);

/* ------------------------------- page -------------------------------- */

export default function Parents() {
  const navigate = useNavigate();

  // auth/profile
  const [profile, setProfile] = useState(null);

  // UI + data
  const [status, setStatus] = useState(""); // no flash
  const [rawPayload, setRawPayload] = useState(null);
  const [childrenCards, setChildrenCards] = useState([]); // built cards
  const [q, setQ] = useState(""); // search/filter

	const hardLogout = () => {
		["idToken", "userProfile", CACHE_LIST_KEY, CACHE_BY_VOUCHER_KEY, CACHE_OWNER_KEY].forEach((k) => {
    		sessionStorage.removeItem(k);
    		localStorage.removeItem(k);
  		});
  		navigate("/");
	};


  // Derived: filtered cards (mobile users often search)
  const filteredCards = useMemo(() => {
    if (!q.trim()) return childrenCards;
    const v = q.trim().toLowerCase();
    return childrenCards.filter((c) => {
      return (
        c.childName.toLowerCase().includes(v) ||
        (c.className || "").toLowerCase().includes(v) ||
        (c.academicYear || "").toLowerCase().includes(v) ||
        (c.voucher || "").toLowerCase().includes(v)
      );
    });
  }, [childrenCards, q]);

  // Build cards helper (pure)
  const buildCards = (assessments, me) => {
    return assessments
      .filter((r) => String(r?.test_taker_name || "").trim())
      .map((r) => {
        const childName = String(r.test_taker_name).trim();
        const classObj =
          (r?.classId && typeof r.classId === "object" && r.classId) || null;

        const ay =
          (classObj?.academicYear &&
            (classObj.academicYear.name
              ? classObj.academicYear.name
              : classObj.academicYear)) ||
          (r?.academicYearId &&
            (r.academicYearId.name ? r.academicYearId.name : r.academicYearId)) ||
          null;

        const g = normalizeGender(r?.gender);
        const a = Number.isFinite(Number(r?.age)) ? Number(r.age) : null;

        return {
          key: `${childName}::${r?._id || r?.voucher || Math.random().toString(36)}`,
          childName,
          voucher: r?.voucher || "",
          takenAt: r?.createdAtVendor || r?.createdAt || null,
          className: classObj?.name || "—",
          academicYear: ay || "—",
          gender: g,
          age: a,
        };
      })
      .sort((x, y) => {
        const tx = x.takenAt ? new Date(x.takenAt).getTime() : 0;
        const ty = y.takenAt ? new Date(y.takenAt).getTime() : 0;
        return ty - tx;
      });
  };

useEffect(() => {
  const init = async () => {
    const store = getStore();
    const token = store?.getItem("idToken");
    if (!token || !store) return hardLogout();

    // try restore profile quickly (optional optimization)
    try {
      const cachedProfile = JSON.parse(store.getItem("userProfile") || "null");
      if (cachedProfile) setProfile(cachedProfile);
    } catch {}

    try {
      // 1) who am I
      const meRes = await fetch(`${BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!meRes.ok) throw new Error("unauthorized");
      const me = await meRes.json();
      store.setItem("userProfile", JSON.stringify(me));
      setProfile(me);

      // redirect teachers
      if (["teacher", "principal", "admin"].includes(me.role)) {
        return navigate("/teacher", { replace: true });
      }

      const cacheOwnerId = me._id || me.id || me.email; // pick whatever is stable

      // 2) now it's safe to read cache for THIS user
      const { list: cachedList } = readCache(cacheOwnerId);
      if (cachedList.length) {
        setChildrenCards(buildCards(cachedList, me));
        setStatus(""); // instant paint from *correct* user
      } else {
        setStatus("Memuat data anak…");
      }

      // 3) always fetch fresh assessments
      const aRes = await fetch(`${BASE_URL}/api/assessments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await aRes.json();
      setRawPayload(payload);
      if (!aRes.ok) throw new Error(payload?.message || "Failed to fetch");

      const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      // always overwrite cache for this user
      writeCache(cacheOwnerId, rows);
      setChildrenCards(buildCards(rows, me));
      setStatus("");
    } catch (err) {
      console.error(err);
      // fall back to whatever cache exists for *current* user
      const store = getStore();
      const token = store?.getItem("idToken");
      let cacheOwnerId = null;

      try {
        const me = JSON.parse(store?.getItem("userProfile") || "null");
        cacheOwnerId = me?._id || me?.id || me?.email || null;
      } catch {}

      const { list } = readCache(cacheOwnerId);
      if (!list.length) {
        setStatus("Failed to load");
        hardLogout();
      } else {
        setChildrenCards(buildCards(list));
        setStatus("");
      }
    }
  };

  init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const useCardsBackground = childrenCards.length > 0;

  return (
    <div
      className={
        useCardsBackground
          ? "min-h-screen bg-[radial-gradient(1100px_500px_at_-15%_-10%,rgba(99,102,241,0.10),transparent),radial-gradient(1000px_500px_at_115%_10%,rgba(16,185,129,0.10),transparent)]"
          : "min-h-screen bg-white"
      }
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
    >
      {/* Header */}
      <Header title="Laporan Asesmen" profile={profile} onLogout={() => hardLogout()} />

      {/* Main */}
      <main className="mx-auto max-w-6xl px-4 sm:px-5 py-6 sm:py-8">
        {/* Title */}
        <div className="mb-5 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Hasil Asesmen
          </h1>
          <p className="text-slate-500 mt-1.5 sm:mt-2 text-sm sm:text-base">
            Pilih salah satu data untuk melihat hasil asesmen dan laporan lengkapnya.
          </p>
        </div>

        {/* Search */}
        <div className="mb-5 sm:mb-6 min-h-[48px]">
          {childrenCards.length > 0 ? (
            <>
              <label htmlFor="search" className="sr-only">Cari anak / kelas / tahun / voucher</label>
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-slate-400 shrink-0 block" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 18a7 7 0 110-14 7 7 0 010 14z"/>
                </svg>
                <input
                  id="search"
                  inputMode="search"
                  autoComplete="off"
                  placeholder="Cari nama anak, kelas, tahun, atau voucher…"
                  className="w-full outline-none text-sm sm:text-base placeholder:text-slate-400"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                {q && (
                  <button type="button" aria-label="Hapus pencarian" onClick={() => setQ("")} className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100 active:scale-95 transition">
                    Bersihkan
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="h-[48px]" />
          )}
        </div>

        {/* Status (kept in DOM to prevent jumps) */}
        <div className="text-slate-500 mb-6 text-sm sm:text-base min-h-[20px]" role="status" aria-live="polite">
          {status || ""}
        </div>

        {/* Loading skeletons (only if absolutely nothing cached) */}
        {status && status.toLowerCase().includes("memuat") && !childrenCards.length && (
          <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
        )}

        {/* Empty */}
        {childrenCards.length === 0 && !status && (
          <div className="mt-16 sm:mt-24 flex flex-col items-center text-center text-slate-500 px-6">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-3 shadow-inner">
              <svg viewBox="0 0 24 24" className="w-7 h-7 sm:w-8 sm:h-8" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A4 4 0 017 17h10a4 4 0 011.879.804M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="font-semibold text-slate-700">Belum ada hasil asesmen</div>
            <div className="text-sm">Hasil asesmen anak Anda akan muncul di sini setelah proses penilaian selesai.</div>
          </div>
        )}

        {/* No matches */}
        {childrenCards.length > 0 && q.trim() && filteredCards.length === 0 && (
          <div className="mt-16 sm:mt-20 flex flex-col items-center text-center text-slate-500">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-3 shadow-inner">
              <svg viewBox="0 0 24 24" className="w-7 h-7 sm:w-8 sm:h-8" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 18a7 7 0 110-14 7 7 0 010 14z" />
              </svg>
            </div>
            <div className="font-semibold text-slate-700">Tidak ada data yang cocok</div>
            <div className="text-sm">
              Kata kunci: <span className="font-medium">&ldquo;{q}&rdquo;</span>. Coba ganti pencarian
              atau{" "}
              <button type="button" onClick={() => setQ("")} className="underline text-indigo-600 hover:text-indigo-700">
                bersihkan pencarian
              </button>.
            </div>
          </div>
        )}

        {/* Cards */}
        {filteredCards.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 xl:grid-cols-3 min-h-[164px]">
            {filteredCards.map((c) => {
              const subline =
                (profile?.role || "").toLowerCase() === "generaluser"
                  ? "Peserta Asesmen"
                  : `${c.className} • ${c.academicYear}`;

              return (
                <button
                  key={c.key}
                  type="button"
                  className="group text-left bg-white rounded-2xl border border-slate-200 shadow-md hover:shadow-xl active:scale-[0.99] transition-all duration-200 p-4 sm:p-6 md:p-7 min-h-[152px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  onClick={() => {
                    const { byVoucher } = readCache();
                    const target = byVoucher?.[c.voucher] || null;

                    navigate(`/report?voucher=${encodeURIComponent(c.voucher)}`, {
                      state: {
                        from: "parents",
                        childName: c.childName,
                        voucher: c.voucher,
                        takenAt: c.takenAt,
                        preloadedAssessment: target, // ⬅️ instant
                      },
                    });
                  }}
                  aria-label={`Lihat laporan ${c.childName}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                      <div className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md shrink-0">
                        <svg viewBox="0 0 24 24" className="w-5 h-5 block" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <div className="text-base sm:text-lg md:text-xl font-semibold text-slate-900 truncate">
                          {c.childName}
                        </div>
                        <div className="text-xs sm:text-sm text-slate-500 truncate">{subline}</div>
                      </div>
                    </div>
                    <div className="rounded-full bg-slate-100 text-slate-400 p-1.5 sm:p-2 group-hover:bg-slate-200 transition shrink-0">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 block" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* meta row */}
                  <div className="mt-4 sm:mt-5 flex flex-wrap items-center gap-2 text-xs sm:text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                      <svg className="w-3.5 h-3.5 block" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M3 9h18M5 21h14a2 2 0 002-2V9H3v10a2 2 0 002 2z" />
                      </svg>
                      <span className="whitespace-nowrap">Diambil: {fmtDate(c.takenAt)}</span>
                    </span>

                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                        c.gender === "male"
                          ? "bg-blue-100 text-blue-800"
                          : c.gender === "female"
                          ? "bg-pink-100 text-pink-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {c.gender === "male" ? "Laki-laki" : c.gender === "female" ? "Perempuan" : "Gender: —"}
                    </span>

                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">
                      Umur: {Number.isFinite(c.age) ? c.age : "—"}
                    </span>

                    {c.voucher ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                        Voucher: <span className="font-mono truncate">{c.voucher}</span>
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Debug / raw JSON */}
      </main>

      {/* Floating Help Button */}
      <Link
        to={{ pathname: "/contact", search: "?from=parents" }}
        state={{ from: "parents" }}
        onClick={() => sessionStorage.setItem("fromParents", "1")}
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-50 flex items-center justify-center w-14 h-14 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-2xl hover:scale-[1.08] transition-all duration-300 ease-out group"
        title="Butuh Bantuan?"
        aria-label="Butuh bantuan?"
        style={{ marginBottom: "max(env(safe-area-inset-bottom), 0px)" }}
      >
        <svg className="w-7 h-7 transition-transform duration-300 group-hover:rotate-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 10a4 4 0 118 0c0 2-2 3-2 3M12 22a10 10 0 100-20 10 10 0 000 20z" />
        </svg>
        <span className="absolute inset-0 rounded-full bg-blue-500 opacity-0 blur-md group-hover:opacity-30 transition-opacity duration-300" />
      </Link>
    </div>
  );
}
